import { httpClient, PaginationParams } from '../core/http';
import { env } from '../config/env';
import {
  CreateKnowledgeSourceRequest,
  KnowledgeSource,
  KnowledgeSourceStats,
  RAGRequest,
  RAGResponse,
  SearchRequest,
  SearchResponse,
  UpdateKnowledgeSourceRequest,
} from '../types';
import { BaseService } from './BaseService';

export class KnowledgeSourceService extends BaseService<
  KnowledgeSource,
  CreateKnowledgeSourceRequest,
  UpdateKnowledgeSourceRequest
> {
  constructor() {
    super('/v1/knowledge-sources');
  }

  async upload(
    data: CreateKnowledgeSourceRequest,
    file: File
  ): Promise<KnowledgeSource> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', data.name);
    formData.append('description', data.description);
    formData.append('file_type', data.file_type);
    formData.append('organization_id', data.organization_id.toString());

    if (data.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    const response = await httpClient.upload<KnowledgeSource>(
      '/v1/knowledge-sources/upload',
      formData
    );
    return response.data;
  }

  async getByOrganization(
    organizationId: number,
    pagination?: PaginationParams
  ): Promise<KnowledgeSource[]> {
    const response = await this.getAll({
      organization_id: organizationId,
      ...pagination,
    });
    return response.items;
  }

  async getActiveByOrganization(
    organizationId: number,
    pagination?: PaginationParams
  ): Promise<KnowledgeSource[]> {
    const response = await this.getAll({
      organization_id: organizationId,
      is_active: true,
      ...pagination,
    });
    return response.items;
  }

  async getByFileType(
    fileType: string,
    organizationId?: number
  ): Promise<KnowledgeSource[]> {
    const params: Record<string, string | number> = { file_type: fileType };
    if (organizationId) {
      params.organization_id = organizationId;
    }
    const response = await this.getAll(params);
    return response.items;
  }

  async getProcessed(organizationId?: number): Promise<KnowledgeSource[]> {
    const params: Record<string, string | number> = {
      processing_status: 'completed',
    };
    if (organizationId) {
      params.organization_id = organizationId;
    }
    const response = await this.getAll(params);
    return response.items;
  }

  async getWithEmbeddings(organizationId?: number): Promise<KnowledgeSource[]> {
    const params: Record<string, string | number> = {
      embeddings_status: 'completed',
    };
    if (organizationId) {
      params.organization_id = organizationId;
    }
    const response = await this.getAll(params);
    return response.items;
  }

  async process(id: number): Promise<void> {
    await httpClient.post(`/v1/knowledge-sources/${id}/process/`, {});
  }

  async generateEmbeddings(id: number): Promise<void> {
    await httpClient.post(`/v1/knowledge-sources/${id}/embeddings/`, {});
  }

  async search(searchRequest: SearchRequest): Promise<SearchResponse> {
    const response = await httpClient.post<SearchResponse>(
      '/v1/knowledge-sources/search/',
      searchRequest
    );
    return response.data;
  }

  async getChunksByKnowledgeSource(
    knowledgeSourceId: number,
    organizationId: number,
    limit: number = 100
  ): Promise<SearchResponse> {
    const searchRequest: SearchRequest = {
      query: '',
      organization_id: organizationId,
      limit,
      similarity_threshold: 0,
      knowledge_source_id: knowledgeSourceId,
    };
    const response = await httpClient.post<SearchResponse>(
      '/v1/knowledge-sources/search/',
      searchRequest
    );
    return response.data;
  }

  async ragQuery(ragRequest: RAGRequest): Promise<RAGResponse> {
    const response = await httpClient.post<RAGResponse>(
      '/v1/knowledge-sources/rag/',
      ragRequest
    );
    return response.data;
  }

  async getStats(organizationId: number): Promise<KnowledgeSourceStats> {
    const response = await httpClient.get<KnowledgeSourceStats>(
      '/v1/knowledge-sources/stats/',
      {
        params: { organization_id: organizationId },
      }
    );
    return response.data;
  }

  async download(id: number): Promise<Blob> {
    const response = await httpClient.get<Blob>(
      `/v1/knowledge-sources/${id}/download`,
      {
        headers: {
          Accept: 'application/octet-stream',
        },
      }
    );
    return response.data;
  }

  getDownloadUrl(id: number): string {
    return `${env.REACT_APP_API_URL}/v1/knowledge-sources/${id}/download/`;
  }
}
